// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { getViewSizeAtPosition } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { HighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import {
  LabelBillboardGeometry,
  type LabelText,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/LabelBillboardGeometry';
import { ViewportScaledGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/ViewportScaledGroup';

/**
 * The billboard's target height expressed as a fraction of the camera frustum height.
 * Keeping this constant means a label always occupies the same share of screen real
 * estate regardless of zoom level.
 */
const SCREEN_SIZE_FRACTION = 0.035;

export class LabelBillboard extends ViewportScaledGroup {
  private readonly billboardGeometry: LabelBillboardGeometry;
  constructor(
    highlightableObjectName: HighlightableObjectName,
    private text: LabelText,
    public group: THREE.Group,
  ) {
    super(SCREEN_SIZE_FRACTION, 1);
    this.billboardGeometry = new LabelBillboardGeometry(highlightableObjectName, this.text);
    this.add(this.billboardGeometry);
    this.group.add(this);
  }

  /**
   * Returns the billboard's current NDC center and half-extents.
   * Used by {@link BendPreviewRenderer}'s overlap resolver.
   * @param camera The camera used to compute the NDC coordinates. The renderer should pass the active camera.
   */
  getNDC(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): {
    center: THREE.Vector2;
    ndcHalfExtents: THREE.Vector2;
  } {
    // Get the position of the billboard in world & NDC space.
    const worldPos = this.billboardGeometry.textBillboard.getWorldPosition(new THREE.Vector3());
    const ndcPos = worldPos.clone().project(camera);

    // Read the effective world-space scale (sprite local scale × group scale) so that
    // the half-extents correctly reflect the viewport-fraction sizing applied by update().
    const worldScale = this.billboardGeometry.textBillboard.getWorldScale(new THREE.Vector3());
    const ndcHalfExtents = new THREE.Vector2(worldScale.x, worldScale.y);

    // NDC space is 2 units wide/tall, so converting this to a unit length will make it a half extent in NDC space.
    if (camera instanceof THREE.PerspectiveCamera) {
      const { height, width } = getViewSizeAtPosition(camera, worldPos);
      ndcHalfExtents.y /= height;
      ndcHalfExtents.x /= width;
    } else {
      ndcHalfExtents.divide(new THREE.Vector2(camera.right - camera.left, camera.top - camera.bottom));
    }

    return {
      center: new THREE.Vector2(ndcPos.x, ndcPos.y),
      ndcHalfExtents,
    };
  }

  highlight() {
    this.billboardGeometry.highlight();
  }

  unhighlight() {
    this.billboardGeometry.unhighlight();
  }

  dispose() {
    this.billboardGeometry.dispose();
    this.remove(this.billboardGeometry);
    this.removeFromParent();
    super.dispose();
  }
}
